import type { PriceOverrideStatus } from "@classes-service-types/price-override-status.ts";
import { useDebounceFn } from "@vueuse/core";
import { computed, type Ref, ref } from "vue";
import { useSearchSessionsPriceOverrides } from "@/providers/priceOverrides/useSearchSessionsPriceOverrides.ts";

export const useSearchSessions = (
  status: Ref<Array<PriceOverrideStatus> | undefined>,
  session: Ref<Array<string>>,
) => {
  const sessionLookupName = ref("");

  const { data: originalSessionOptions, isLoading: isLoadingOriginalSessionsOptions } =
    useSearchSessionsPriceOverrides(status, undefined, session);
  const { data: searchSessionOptions, isLoading: isLoadingSearchSessionsOptions } = useSearchSessionsPriceOverrides(
    status,
    sessionLookupName,
  );

  const sessionOptions = computed(() => {
    const map = new Map();
    originalSessionOptions?.value?.data?.items?.forEach((val) => {
      map.set(val.id, val.name);
    });
    searchSessionOptions?.value?.data?.items?.forEach((val) => {
      map.set(val.id, val.name);
    });
    return Array.from(map.entries())
      .map(([key, value]) => ({
        id: key,
        name: value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  const debouncedSessionNameUpdate = useDebounceFn(
    (value) => {
      sessionLookupName.value = value.value;
    },
    1000,
    { maxWait: 5000 },
  );

  return {
    isLoadingOriginalSessionsOptions,
    isLoadingSearchSessionsOptions,
    sessionOptions,
    debouncedSessionNameUpdate,
    sessionLookupName,
  };
};
